import React, { useState, useEffect, useRef } from "react";
import { CircleUser, Briefcase, Wallet, FolderOpen, Phone, GraduationCap, Building, Lock, Upload, Download, FileText, CheckCircle, AlertCircle, X, Heart, Camera, Eye, EyeOff, MapPin, Building2, CreditCard, Fingerprint, Users, Mail, Cake, Flag, ShieldCheck, Pencil, Copy, ChevronRight } from "lucide-react";
import api, { BASE } from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { Cd, Bg, Bt, Sl, Tbl, Av, Sec, SearchSelect } from "../components";
import { ReportingManager } from "../components/ReportingManager";
import { notify } from "../notify";
import { fmtDMY } from "../dateFormat";
import { validateFaceDetectable } from "../utils/faceValidate";
import { getAppTimezone } from "../utils/timezone";
import { EmployeeCtcSummary } from "../components/EmployeeCtcSummary";
import { MyDeductionsCard } from "../components/DeductionsManager";
import { MyAdditionsCard } from "../components/AdditionsManager";

// Clamp crop offset so image edges always cover the circle (no white space).
function clampCrop(state) {
  if (!state) return state;
  const cropSize = 220;
  const bs = state.baseScale || 1;
  const z = state.zoom || 1;
  const w = (state.imgW || cropSize) * bs * z;
  const h = (state.imgH || cropSize) * bs * z;
  const maxX = Math.max(0, (w - cropSize) / 2);
  const maxY = Math.max(0, (h - cropSize) / 2);
  return {
    ...state,
    offsetX: Math.max(-maxX, Math.min(maxX, state.offsetX || 0)),
    offsetY: Math.max(-maxY, Math.min(maxY, state.offsetY || 0)),
  };
}

// Photo upload modal — file picker (with drag/drop) or live camera capture,
// both feeding the same crop + upload pipeline. Pulled out of MyProfilePage
// as its own component (rather than an inline render-time IIFE) so it can
// hold its own hooks for the camera stream/video element.
function PhotoUploadModal({ photoCrop, setPhotoCrop, onSaved }) {
  const cropSize = 220;
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cropperRef = useRef(null);
  const [camOpen, setCamOpen] = useState(false);
  const [camError, setCamError] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => () => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); }, []);

  // Non-passive wheel listener so e.preventDefault() actually stops scroll.
  useEffect(() => {
    const el = cropperRef.current;
    if (!el || !photoCrop?.src) return;
    const onWheel = (e) => {
      e.preventDefault();
      setPhotoCrop(p => clampCrop({ ...p, zoom: Math.max(1, Math.min(4, (p.zoom || 1) + (e.deltaY > 0 ? -0.1 : 0.1))) }));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [photoCrop?.src]);

  const onFileSelect = (file) => { if (!file || !file.type.startsWith("image/")) return; const reader = new FileReader(); reader.onload = (ev) => { const img = new Image(); img.src = ev.target.result; img.onload = () => { const baseScale = cropSize / Math.min(img.width, img.height); setPhotoCrop(p => ({ ...p, src: ev.target.result, file, zoom: 1, offsetX: 0, offsetY: 0, imgW: img.width, imgH: img.height, baseScale })); }; }; reader.readAsDataURL(file); };

  const startCamera = async () => {
    setCamError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 480 }, height: { ideal: 480 } } });
      streamRef.current = stream;
      setCamOpen(true);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); } }, 0);
    } catch (e) { setCamError(e.name === "NotAllowedError" ? "Camera access denied. Allow camera access and try again." : "Could not access camera: " + e.message); }
  };
  const stopCamera = () => { if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; } setCamOpen(false); };
  const capturePhoto = () => {
    const v = videoRef.current; if (!v) return;
    const c = document.createElement("canvas"); c.width = v.videoWidth || 480; c.height = v.videoHeight || 480;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => { if (blob) onFileSelect(new File([blob], "camera-photo.jpg", { type: "image/jpeg" })); stopCamera(); }, "image/jpeg", 0.9);
  };

  const doMyUpload = async () => { if (!photoCrop.src) return; setUploadingPhoto(true); const c = document.createElement("canvas"); c.width = 300; c.height = 300; const ctx = c.getContext("2d"); const img = new Image(); img.src = photoCrop.src; await new Promise(r => { img.onload = r; }); const bs = photoCrop.baseScale || 1; const z = photoCrop.zoom; const totalScale = bs * z; const drawW = img.width * totalScale; const drawH = img.height * totalScale; const outScale = 300 / cropSize; const dx = (cropSize / 2 - drawW / 2 + photoCrop.offsetX) * outScale; const dy = (cropSize / 2 - drawH / 2 + photoCrop.offsetY) * outScale; ctx.beginPath(); ctx.arc(150, 150, 150, 0, Math.PI * 2); ctx.clip(); ctx.drawImage(img, dx, dy, drawW * outScale, drawH * outScale); const faceCheck = await validateFaceDetectable(c); if (!faceCheck.ok) { notify(faceCheck.message, { title: "No Face Detected", variant: "error" }); setUploadingPhoto(false); return; } c.toBlob(async (blob) => { const fd = new FormData(); fd.append("file", blob, "profile-me.jpg"); try { const res = await fetch(BASE + "/api/employees/me/profile-photo", { method: "POST", headers: { "Authorization": "Bearer " + localStorage.getItem("hrms_token") }, body: fd }); const data = await res.json(); if (!res.ok) { notify(data.error || "Upload failed", { title: "Error", variant: "error" }); } else { notify("Profile photo updated", { title: "Saved", variant: "success" }); onSaved?.(); } setPhotoCrop(null); } catch (err) { notify(err.message || "Upload failed", { title: "Error", variant: "error" }); } finally { setUploadingPhoto(false); } }, "image/jpeg", 0.9); };

  return <div className="fixed inset-0 animate-fadeIn bg-black/60 z-[300] flex items-center justify-center p-3" onMouseDown={() => { stopCamera(); setPhotoCrop(null); }}>
    <div className="animate-modalIn bg-bg1 rounded-[20px] p-5 sm:p-7 w-full max-w-[600px] max-h-[90vh] overflow-auto shadow-[0_24px_80px_rgba(0,0,0,0.25)]" onMouseDown={e => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-5">
        <div>
          <div className="text-base font-extrabold text-t0">Upload Photo</div>
          <div className="text-[14px] text-t3">{camOpen ? "Center your face and capture" : "Drag to reposition · Scroll to zoom"}</div>
        </div>
        <button onClick={() => { stopCamera(); setPhotoCrop(null); }} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
      </div>
      {!photoCrop.src && !camOpen ? <div>
        <div
          onDragOver={e => { e.preventDefault(); }}
          onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) onFileSelect(f); }}
          className="border-2 border-dashed border-b1 rounded-2xl p-10 text-center cursor-pointer bg-bg2"
          onClick={() => { const inp = document.createElement("input"); inp.type = "file"; inp.accept = ".jpg,.jpeg,.png,.webp"; inp.onchange = e => { const f = e.target.files[0]; if (f) onFileSelect(f); }; inp.click(); }}
        >
          <Upload size={32} color={K.p} className="mx-auto mb-3" />
          <div className="text-[15px] font-bold text-t0 mb-1">Drop your photo here</div>
          <div className="text-[14px] text-t3">or click to browse</div>
        </div>
        <div className="flex items-center gap-2 my-3"><div className="flex-1 h-px bg-b1" /><span className="text-[12px] text-t3">OR</span><div className="flex-1 h-px bg-b1" /></div>
        <Bt v="secondary" full icon={Camera} onClick={startCamera}>Take Photo</Bt>
        {camError && <div className="text-[14px] mt-2 text-center" style={{ color: K.r }}>{camError}</div>}
      </div> : camOpen ? <div className="flex flex-col items-center">
        <div className="rounded-full overflow-hidden border-[3px] border-p bg-bg2" style={{ width: cropSize, height: cropSize }}>
          <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }} />
        </div>
        <div className="flex gap-2 mt-4 w-full">
          <Bt v="secondary" full onClick={stopCamera}>Cancel</Bt>
          <Bt full icon={Camera} onClick={capturePhoto}>Capture</Bt>
        </div>
      </div> : <div>
        <div className="flex flex-col items-center mb-4">
          <div
            ref={cropperRef}
            className="rounded-full overflow-hidden relative cursor-grab select-none border-[3px] border-p"
            style={{ width: cropSize, height: cropSize }}
            onMouseDown={e => { e.preventDefault(); setPhotoCrop(p => ({ ...p, dragging: true, _startX: e.clientX - p.offsetX, _startY: e.clientY - p.offsetY })); }}
            onMouseMove={e => { if (!photoCrop.dragging) return; setPhotoCrop(p => clampCrop({ ...p, offsetX: e.clientX - p._startX, offsetY: e.clientY - p._startY })); }}
            onMouseUp={() => setPhotoCrop(p => ({ ...p, dragging: false }))}
            onMouseLeave={() => setPhotoCrop(p => ({ ...p, dragging: false }))}
          >{(() => { const bs = photoCrop.baseScale || 1; const z = photoCrop.zoom; const w = (photoCrop.imgW || cropSize) * bs * z; const h = (photoCrop.imgH || cropSize) * bs * z; return <img src={photoCrop.src} alt="crop" draggable={false} className="absolute pointer-events-none" style={{ left: cropSize / 2 - w / 2 + photoCrop.offsetX, top: cropSize / 2 - h / 2 + photoCrop.offsetY, width: w, height: h, maxWidth: 'none', maxHeight: 'none' }} />; })()}</div>
        </div>
        <div className="flex items-center gap-[10px] mb-4 px-5">
          <span className="text-[14px] text-t3">−</span>
          <input type="range" min="1" max="4" step="0.05" value={photoCrop.zoom} onChange={e => setPhotoCrop(p => clampCrop({ ...p, zoom: parseFloat(e.target.value) }))} className="flex-1" style={{ accentColor: K.p }} />
          <span className="text-[14px] text-t3">+</span>
        </div>
        <div className="flex gap-2">
          <Bt v="secondary" full disabled={uploadingPhoto} onClick={() => setPhotoCrop(p => ({ ...p, src: null, file: null, zoom: 1, offsetX: 0, offsetY: 0 }))}>Change Photo</Bt>
          <Bt full icon={CheckCircle} disabled={uploadingPhoto} onClick={doMyUpload}>{uploadingPhoto ? "Uploading…" : "Save Photo"}</Bt>
        </div>
      </div>}
    </div>
  </div>;
}

// Presence config + formatting shared by the "My Team" flat view and the
// recursive org-tree below. Module scope (not inline in MyProfilePage) so
// TeamTreeNode — which needs to persist its own expand/collapse state
// across parent re-renders — can use them without being redefined (and
// therefore remounted, losing that state) on every render.
const TEAM_PRESENCE = {
  in:      { c: K.g,  label: "In" },
  out:     { c: K.t2, label: "Checked out" },
  leave:   { c: K.a,  label: "On leave" },
  holiday: { c: K.pu, label: "Holiday" },
  absent:  { c: K.r,  label: "Absent" },
  pending: { c: K.r,  label: "Yet to check-in" },
};
const teamHHMM = (t) => {
  if (!t) return null;
  const d = new Date(t);
  if (isNaN(d)) return null;
  try {
    return new Intl.DateTimeFormat("en-IN", { timeZone: getAppTimezone(), hour: "numeric", minute: "2-digit", hour12: true }).format(d);
  } catch (e) { return null; }
};
const teamPresenceText = (p) => {
  if (!p) return { c: K.t3, label: "—", detail: null };
  const cfg = TEAM_PRESENCE[p.state] || { c: K.t3, label: p.label || "—" };
  let detail = null;
  if (p.state === "in" && teamHHMM(p.checkIn)) detail = `since ${teamHHMM(p.checkIn)}`;
  else if (p.state === "out" && teamHHMM(p.checkOut)) detail = `at ${teamHHMM(p.checkOut)}`;
  return { c: cfg.c, label: p.label || cfg.label, detail };
};

// Recursive, lazily-expanding org-tree node. Click the chevron to expand a
// person's own direct reports inline (fetched once on first expand, then
// cached in this node's own state) instead of the old behaviour where
// clicking someone replaced the whole view and lost everything above them
// — this reads as an actual tree the deeper you go, with a connecting
// line per level and a quick drop-in animation on expand.
// Org-chart node card — conic-gradient ring color communicates tier at a
// glance (manager=purple, self=blue+prominent, report=teal), matching the
// same ring treatment the Dashboard's celebration photos use elsewhere in
// the app. `onClick` is caller-supplied: the manager card uses it to
// navigate up (re-root the tree there); a report card with its own
// reports uses it to expand/collapse in place — the card itself doesn't
// know or care which.
const TeamNodeCard = ({ node, tier, onClick, expandState }) => {
  const ring = tier === "self" ? K.p : tier === "manager" ? K.pu : K.tl;
  const st = teamPresenceText(node.presence);
  return (
    <div
      onClick={onClick}
      className={`group relative flex flex-col items-center gap-[8px] px-4 py-3 rounded-2xl border bg-bg1 transition-all duration-200 w-[156px] ${
        tier === "self" ? "border-p shadow-[0_8px_24px_rgba(37,99,235,0.14)]" : "border-b0 shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
      } ${onClick ? "cursor-pointer hover:-translate-y-[2px] hover:border-p hover:shadow-[0_10px_22px_rgba(15,23,42,0.10)]" : ""}`}
    >
      <div className="relative rounded-full p-[2.5px]" style={{ background: `conic-gradient(from 180deg, ${ring}, ${K.pl}, ${ring})` }}>
        <div className="rounded-full bg-bg1 p-[2px]"><Av nm={node.name} sz={44} src={node.photo} /></div>
      </div>
      <div className="text-center min-w-0 w-full">
        <div className="text-[13px] font-bold text-t0 truncate">{node.name}</div>
        <div className="text-[10.5px] text-t3 truncate mt-[1px]">{node.designation || node.id}</div>
      </div>
      <span className="flex items-center gap-[4px]">
        <span className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ background: st.c }} />
        <span className="text-[10.5px] font-semibold whitespace-nowrap" style={{ color: st.c }}>{st.label}</span>
      </span>
      {tier !== "self" && (node.email || node.phone) && (
        <div className="flex items-center gap-[4px] mt-[-2px]">
          {node.email && (
            <a href={`mailto:${node.email}`} title={node.email} onClick={e => e.stopPropagation()}
               className="p-[4px] rounded-md border border-b0 text-t3 hover:text-p hover:border-p transition-colors">
              <Mail size={11} />
            </a>
          )}
          {node.phone && (
            <button type="button" title={node.phone} onClick={async e => {
              e.stopPropagation();
              try { await navigator.clipboard.writeText(node.phone); notify(`Copied: ${node.phone}`, { title: "Copied", variant: "success" }); }
              catch { notify(node.phone, { title: "Phone number" }); }
            }}
               className="p-[4px] rounded-md border border-b0 bg-transparent text-t3 hover:text-p hover:border-p transition-colors cursor-pointer">
              <Copy size={11} />
            </button>
          )}
        </div>
      )}
      {expandState && (
        <span className="absolute -bottom-[9px] left-1/2 -translate-x-1/2 text-[10px] font-bold px-[8px] py-[2px] rounded-full bg-bg1 border border-b0 text-t3 flex items-center gap-[3px] shadow-sm">
          <ChevronRight size={9} className={`transition-transform duration-200 ${expandState === "open" ? "rotate-90" : ""}`} />
          {node.reportsCount}
        </span>
      )}
    </div>
  );
};

// Short vertical reporting-line stub with a joint dot where it meets the
// card below — reads as a connector, not a bare rule.
const TeamStem = ({ h = 22 }) => (
  <div className="flex flex-col items-center flex-shrink-0" style={{ height: h }}>
    <div className="w-[2px] flex-1" style={{ background: `linear-gradient(${K.b1}, ${K.b2})` }} />
    <div className="w-[6px] h-[6px] rounded-full -mt-[1px]" style={{ background: K.b2 }} />
  </div>
);

const TeamSectionLabel = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.08em] text-t3 font-extrabold mb-3">{children}</div>
);

// A recursive branch of the org chart: this person's card, and — once
// expanded — their own direct reports as a fresh branching row below,
// fetched (and cached) lazily on first expand rather than loading the
// whole org up front. Keep clicking down as many levels as the chain
// actually goes, each one reading as a real branch off the last.
const TeamTreeBranch = ({ node }) => {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState(null);
  const [loading, setLoading] = useState(false);
  const hasReports = (node.reportsCount || 0) > 0;

  const toggle = async () => {
    if (!hasReports) return;
    if (!expanded && children === null) {
      setLoading(true);
      try {
        const d = await api.get('/api/employees/' + node.id + '/team-node');
        setChildren(d?.directReports || []);
      } catch { setChildren([]); }
      setLoading(false);
    }
    setExpanded(v => !v);
  };

  return (
    <div className="flex flex-col items-center">
      <TeamNodeCard node={node} tier="report" onClick={hasReports ? toggle : undefined} expandState={hasReports ? (expanded ? "open" : "closed") : null} />
      {expanded && (
        <div className="flex flex-col items-center mt-[18px] animate-dropIn">
          <TeamStem h={16} />
          {loading && <div className="text-[12px] text-t3 py-2">Loading…</div>}
          {children && children.length === 0 && <div className="text-[12px] text-t3 py-2 italic">No direct reports.</div>}
          {children && children.length > 0 && (
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-7 pt-4" style={{ borderTop: `2px solid ${K.b1}` }}>
              {children.map(c => (
                <div key={c.id} className="relative">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-[2px] h-4" style={{ background: K.b1 }} />
                  <TeamTreeBranch node={c} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const MyProfilePage = ({ myProfile, photoCrop, setPhotoCrop, mustChangePassword, onPhotoUpdated, roleLabel, role, can = () => true }) => {
  // Security tab (self-service change password) is hidden by default —
  // it only appears once an admin has issued a password-reset request for
  // this account (must_change_password), and opens straight to it so the
  // user notices it without having to hunt for it.
  const [myTab, setMyTab] = useState(mustChangePassword ? "security" : "basic");
  const [viewPhoto, setViewPhoto] = useState(null);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "", showCur: false, showNext: false, showConfirm: false, saving: false, msg: "", err: "" });
  const [taxFY, setTaxFY] = useState(String(new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1));
  const [taxData, setTaxData] = useState(null);
  const [structure, setStructure] = useState(null);
  const [mySlips, setMySlips] = useState(null);
  const [pickedPeriod, setPickedPeriod] = useState('');
  // Salary Revision Timeline — mirrors the admin Payroll tab so employees
  // can see their own CTC / Basic / HRA / Allowance history without
  // having to ask HR for a snapshot.
  const [salaryHistory, setSalaryHistory] = useState(null);
  // GHI (Group Health Insurance) status + covered family — read-only for
  // the employee. Same endpoint the Compliance drill-down uses.
  const [ghi, setGhi] = useState(null);
  const [showEmergencyPhone, setShowEmergencyPhone] = useState(false);

  // Pull the employee's own salary structure so they can see what their
  // CTC is composed of (read-only — the editor stays on the admin side).
  const myId = myProfile?.basic?.employee_id || myProfile?.employee_id;
  useEffect(() => {
    if (!myId) return;
    api.get('/api/employees/' + myId + '/salary-structure')
       .then(d => setStructure(d))
       .catch(() => setStructure(null));
  }, [myId]);

  // Pull the actual list of slips that exist for this employee so the
  // Salary Slips picker only offers periods they really have on file.
  useEffect(() => {
    if (!myId) return;
    api.get('/api/payroll/slips?employee_id=' + myId)
       .then(d => {
         const arr = Array.isArray(d) ? d : [];
         const sorted = [...arr].sort((a, b) => (b.period || '').localeCompare(a.period || ''));
         setMySlips(sorted);
         if (sorted.length) setPickedPeriod(sorted[0].period);
       })
       .catch(() => setMySlips([]));
  }, [myId]);

  // Salary Revision Timeline for the "Salary & Payroll" tab.
  useEffect(() => {
    if (!myId) return;
    api.get('/api/employees/' + myId + '/salary-history')
       .then(d => setSalaryHistory(Array.isArray(d) ? d : []))
       .catch(() => setSalaryHistory([]));
  }, [myId]);

  useEffect(() => {
    if (!myId) return;
    api.get('/api/employees/' + myId + '/ghi-summary')
       .then(d => setGhi(d))
       .catch(() => setGhi(null));
  }, [myId]);

  // My Team — a small, navigable org-tree: whoever is "focused" (starts as
  // me) shown with their manager above and direct reports below. Clicking
  // any node just re-focuses on that person's id and re-fetches, so the
  // whole reporting chain is walkable without leaving this tab.
  const [teamFocusId, setTeamFocusId] = useState(null); // null = "me" (myId)
  const [teamNode, setTeamNode] = useState(null); // null = loading, false = not found/error
  const effectiveTeamId = teamFocusId || myId;
  useEffect(() => {
    if (!effectiveTeamId) return;
    setTeamNode(null);
    api.get('/api/employees/' + effectiveTeamId + '/team-node')
       .then(d => setTeamNode(d))
       .catch(() => setTeamNode(false));
  }, [effectiveTeamId]);

  // Org-wide employee list, loaded lazily the first time My Team is opened
  // by someone with org-wide visibility elsewhere in the app (admin/HR) —
  // powers the "jump to anyone" root picker so they can explore an
  // existing manager's real tree without it depending on having direct
  // reports of their own.
  const canBrowseOrgTree = can('employees', 'basic', null, 'edit');
  const [orgEmployees, setOrgEmployees] = useState(null);
  useEffect(() => {
    if (myTab === 'myteam' && canBrowseOrgTree && orgEmployees === null) {
      api.get('/api/employees?limit=1000')
         .then(d => setOrgEmployees(d.data || d || []))
         .catch(() => setOrgEmployees([]));
    }
  }, [myTab, canBrowseOrgTree, orgEmployees]);

  if (!myProfile) return <div className="text-center p-10 text-t3">Loading your profile...</div>;
  const raw = myProfile; const b = raw.basic || raw; const emp = raw.employment || raw;
  const mfe = { ...b, ...emp, salary: raw.salary || {}, documents: raw.documents || [], emergency: Array.isArray(raw.emergency) ? raw.emergency[0] || {} : raw.emergency || {}, education: Array.isArray(raw.education) ? raw.education[0] || {} : raw.education || {}, experience: raw.experience || [], leave_balance: raw.leave_balance || {}, home_address: raw.home_address || null, home_latitude: raw.home_latitude || null, home_longitude: raw.home_longitude || null, home_radius_meters: raw.home_radius_meters || null, attendance_policy: raw.attendance_policy || 'office_only' };
  const myTabs = [["basic", "Basic Info", CircleUser], ["employment", "Employment", Briefcase], ["salary", "Salary Structure", Wallet], ["documents", "Documents", FolderOpen], ["ghi", "GHI", Heart], ["emergency", "Emergency", Phone], ["education", "Education", GraduationCap], ["experience", "Experience", Building], ["myteam", "My Team", Users], ["security", "Security", Lock]]
    .filter(([k]) => k === "security" || can('myprofile', k, null, 'view'));

  // Soft "field pill" used on the redesigned Basic Info cards — same idea
  // as RO above but matching the new card visual language (icon-less,
  // label-over-value, light-blue tint per the reference mockup).
  const Fld = ({ label, value, icon: Ic, mono, children }) => <div className="bg-pg rounded-xl px-[14px] py-[10px]">
    <div className="text-[11px] uppercase tracking-wide text-t3 mb-[3px]">{label}</div>
    {children || <div className={`flex items-center gap-[6px] text-[14px] font-semibold text-t0 ${mono ? "font-mono" : ""}`}>{Ic && <Ic size={14} className="text-t3 flex-shrink-0" />}<span className="truncate">{value || "—"}</span></div>}
  </div>;

  const photo = raw.profile_photo || mfe.profile_photo;
  const yearsOfService = (() => {
    if (!mfe.join_date) return null;
    const jd = new Date(mfe.join_date);
    if (isNaN(jd)) return null;
    const yrs = (Date.now() - jd.getTime()) / (365.25 * 86400000);
    return yrs < 1 ? `${Math.round(yrs * 12)} mo` : `${Math.floor(yrs)} yr${Math.floor(yrs) === 1 ? "" : "s"}`;
  })();
  const leaveBalanceTotal = (() => {
    const lb = mfe.leave_balance || {};
    const sum = ["CL", "SL", "EL"].reduce((s, k) => s + (parseFloat(lb[k]?.balance) || 0), 0);
    return Object.keys(lb).length ? sum : null;
  })();
  const isActive = mfe.status === "Active" || mfe.probation === "Confirmed" || mfe.status === "Confirmed";
  // Mask a statutory ID down to its first/last few characters — same
  // pattern the mockup used, applied to our own real fields (PAN, Aadhaar,
  // UAN) instead of a fabricated "Diplomatic Passport".
  const maskId = (v, head = 4, tail = 2) => {
    const s = String(v || "").trim();
    if (!s) return null;
    if (s.length <= head + tail) return s;
    return s.slice(0, head) + "••••" + s.slice(-tail);
  };
  const emg = mfe.emergency || {};

  return <div>
    {/* ── Profile banner — same brand gradient across the whole app's
        "hero" surfaces (Dashboard, this page). ── */}
    <div className="relative w-full rounded-2xl overflow-hidden mb-0" style={{ background: "linear-gradient(90deg, #0a2364 0%, #0b4d79 55%, #059669 100%)" }}>
      <div className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay">
        <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1200 240" xmlns="http://www.w3.org/2000/svg">
          <circle cx="100" cy="220" opacity="0.25" r="180" stroke="white" strokeWidth="48" />
          <circle cx="340" cy="240" opacity="0.2" r="220" stroke="white" strokeWidth="56" />
          <path d="M750 -40C860 60 980 120 1220 100" opacity="0.15" stroke="white" strokeWidth="64" />
        </svg>
      </div>
      <div className="relative z-10 px-5 sm:px-7 pt-7 pb-[72px] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-[6px] text-white/70 text-[12px] font-bold uppercase tracking-wide">
            <Building2 size={13} />
            {mfe.company?.name || "Zucol"}{mfe.department ? ` · ${mfe.department}` : ""}
          </div>
          <h1 className="m-0 mt-[6px] text-white text-[24px] sm:text-[28px] font-black leading-tight tracking-tight">My Profile</h1>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {myId && (
            // Jumps to the same Employee Profile modal used from the
            // Employees list, opened on this person's own record — that
            // modal already knows whether the logged-in role can actually
            // edit a field or just view it, same rules either way, this is
            // just a shortcut into it rather than a separate edit surface.
            <button onClick={() => { window.location.hash = 'employees?id=' + encodeURIComponent(myId); }} className="flex items-center gap-[7px] bg-white/15 hover:bg-white/25 transition-colors text-white text-[14px] font-bold rounded-xl px-4 py-[10px] border border-white/20 cursor-pointer">
              <Pencil size={14} /> Edit Profile
            </button>
          )}
          <button onClick={() => setMyTab("documents")} className="flex items-center gap-[7px] bg-white/15 hover:bg-white/25 transition-colors text-white text-[14px] font-bold rounded-xl px-4 py-[10px] border border-white/20 cursor-pointer">
            <FolderOpen size={14} /> My Documents
          </button>
        </div>
      </div>
    </div>

    {/* ── Identity card — overlaps the banner like the reference, and sits
        narrower than the banner (inset on both sides) so more of the
        gradient shows around it instead of the card running edge to edge. ── */}
    <div className="relative z-10 -mt-12 mb-[14px] px-4 sm:px-8">
      <div className="bg-s rounded-2xl border border-b0 p-5 shadow-[0_2px_10px_rgba(15,23,42,0.08)] flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative flex-shrink-0">
            <div className={photo ? "cursor-pointer" : ""} onClick={() => photo && setViewPhoto(photo)}>
              <Av nm={mfe.name || "?"} sz={84} rn={K.p} src={photo} />
            </div>
            <button onClick={() => setPhotoCrop({ src: null, zoom: 1, offsetX: 0, offsetY: 0, dragging: false, file: null, _myProfile: true })} className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-p flex items-center justify-center cursor-pointer p-0 border-2 border-s shadow-[0_2px_6px_rgba(0,0,0,0.25)]">
              <Camera size={14} color="#fff" />
            </button>
          </div>
          {viewPhoto && <div className="fixed inset-0 animate-fadeIn bg-black/85 z-[400] flex items-center justify-center p-6" onMouseDown={() => setViewPhoto(null)}>
            <button onClick={() => setViewPhoto(null)} className="fixed top-4 right-4 bg-white/10 border border-white/20 text-white cursor-pointer rounded-full w-9 h-9 flex items-center justify-center"><X size={18} /></button>
            <img src={viewPhoto.startsWith("http") ? viewPhoto : uploadUrl(viewPhoto)} alt={mfe.name} className="animate-modalIn max-w-[90vw] max-h-[85vh] rounded-2xl object-contain shadow-2xl" onMouseDown={e => e.stopPropagation()} />
          </div>}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-[8px]">
              <span className="text-[22px] font-extrabold text-t0 tracking-tight">{mfe.name || "—"}</span>
              <Bg c={isActive ? K.g : K.a} dot sm>{(mfe.status || mfe.probation || "—").toUpperCase()}</Bg>
              {roleLabel && <Bg c={K.p} sm>{roleLabel.toUpperCase()}</Bg>}
            </div>
            <div className="text-[14px] font-semibold text-t2 mt-[2px]">{mfe.designation || mfe.role?.title || "—"}</div>
            <div className="flex flex-wrap items-center gap-x-[14px] gap-y-[3px] mt-[6px] text-[13px] text-t3">
              <span className="font-mono font-semibold bg-pg text-t1 px-[8px] py-[2px] rounded-md">ID: {mfe.employee_id}</span>
              {mfe.office?.city || mfe.office?.name ? <span className="flex items-center gap-1"><MapPin size={13} className="text-p" />{mfe.office?.city || mfe.office?.name}</span> : null}
              {mfe.join_date && <span className="flex items-center gap-1"><Cake size={13} className="text-tl" />Joined {fmtDMY(mfe.join_date)}{yearsOfService ? ` (${yearsOfService})` : ""}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center flex-shrink-0">
          <ReportingManager
            id={mfe.manager_id}
            name={mfe.manager}
            photo={mfe.manager_photo}
            designation={mfe.manager_designation}
          />
        </div>
      </div>
    </div>

    <div className="flex gap-[6px] mb-[14px] flex-wrap whitespace-nowrap bg-pg rounded-2xl p-[6px]">
      {myTabs.map(([k, l, Ic]) => (
        <button
          key={k}
          onClick={() => setMyTab(k)}
          className={`flex items-center gap-[6px] px-[14px] py-[8px] rounded-xl border-0 text-[14px] font-semibold cursor-pointer whitespace-nowrap transition-colors ${myTab === k ? "text-white" : "bg-transparent text-t2 hover:bg-pb"}`}
          style={myTab === k ? { background: K.p } : undefined}
        ><Ic size={13} />{l}</button>
      ))}
    </div>
    {myTab === "basic" && <div className="grid grid-cols-1 lg:grid-cols-12 gap-[14px]">
      <div className="lg:col-span-8 flex flex-col gap-[14px]">
        <Cd>
          <Sec icon={CircleUser} title="Personal Information" color={K.p} />
          <div className="text-[13px] text-t3 -mt-[10px] mb-[12px]">Core identity details as per HR records</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-[10px]">
            <Fld label="Full Name" value={mfe.name} />
            <Fld label="Date of Birth" value={mfe.dob ? fmtDMY(mfe.dob) : null} icon={Cake} />
            <Fld label="Gender" value={mfe.gender === "M" ? "Male" : mfe.gender === "F" ? "Female" : mfe.gender} />
            <Fld label="Blood Group" value={mfe.blood_group} />
            <Fld label="Marital Status" value={mfe.marital_status} />
            <Fld label="Nationality" value={mfe.nationality} icon={Flag} />
          </div>
        </Cd>
        <Cd>
          <Sec icon={Mail} title="Contact Information" color={K.bl} />
          <div className="text-[13px] text-t3 -mt-[10px] mb-[12px]">Workplace routing and personal telecommunication channels</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px]">
            <Fld label="Work Email" value={mfe.email} icon={Mail} mono />
            <Fld label="Personal Email" value={mfe.personal_email} icon={Mail} mono />
            <Fld label="Mobile" value={mfe.phone} icon={Phone} mono />
            <Fld label="Work Phone" value={mfe.work_phone} icon={Phone} mono />
          </div>
        </Cd>
        <Cd>
          <Sec icon={MapPin} title="Address" color={K.tl} />
          <div className="text-[13px] text-t3 -mt-[10px] mb-[12px]">
            Residential address on file{mfe.address_current && mfe.address_permanent && mfe.address_current === mfe.address_permanent ? " · same as permanent" : ""}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
            <div className="bg-pg rounded-xl px-[14px] py-[10px]">
              <div className="text-[11px] uppercase tracking-wide text-t3 mb-[3px]">Current Residential Address</div>
              <div className="text-[14px] font-semibold text-t0">{mfe.address_current || "—"}</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-[8px] pt-[8px] border-t border-b0">
                <div><div className="text-[10px] uppercase text-t3">City</div><div className="text-[13px] font-semibold text-t1">{mfe.city || "—"}</div></div>
                <div><div className="text-[10px] uppercase text-t3">State</div><div className="text-[13px] font-semibold text-t1">{mfe.state || "—"}</div></div>
                <div><div className="text-[10px] uppercase text-t3">Country</div><div className="text-[13px] font-semibold text-t1">{mfe.country || "—"}</div></div>
                <div><div className="text-[10px] uppercase text-t3">PIN Code</div><div className="text-[13px] font-mono font-semibold text-t1">{mfe.pin_code || "—"}</div></div>
              </div>
            </div>
            <div className="bg-pg rounded-xl px-[14px] py-[10px]">
              <div className="text-[11px] uppercase tracking-wide text-t3 mb-[3px]">Permanent Address</div>
              <div className="text-[14px] font-semibold text-t0">{mfe.address_permanent || "—"}</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-[8px] pt-[8px] border-t border-b0">
                <div><div className="text-[10px] uppercase text-t3">City</div><div className="text-[13px] font-semibold text-t1">{mfe.city || "—"}</div></div>
                <div><div className="text-[10px] uppercase text-t3">State</div><div className="text-[13px] font-semibold text-t1">{mfe.state || "—"}</div></div>
                <div><div className="text-[10px] uppercase text-t3">Country</div><div className="text-[13px] font-semibold text-t1">{mfe.country || "—"}</div></div>
                <div><div className="text-[10px] uppercase text-t3">PIN Code</div><div className="text-[13px] font-mono font-semibold text-t1">{mfe.pin_code || "—"}</div></div>
              </div>
            </div>
          </div>
        </Cd>
      </div>
      <div className="lg:col-span-4 flex flex-col gap-[14px]">
        <Cd>
          <Sec icon={Briefcase} title="Organization Details" color={K.p} />
          <div className="flex flex-col divide-y divide-b0">
            <div className="pb-[8px] flex items-center justify-between"><span className="text-[13px] text-t2">Company</span><span className="text-[14px] font-bold text-t0">{mfe.company?.name || "—"}</span></div>
            <div className="py-[8px] flex items-center justify-between"><span className="text-[13px] text-t2">Department</span><span className="text-[14px] font-bold text-t0">{mfe.department || "—"}</span></div>
            <div className="py-[8px] flex items-center justify-between"><span className="text-[13px] text-t2">Reporting Manager</span><span className="text-[14px] font-bold text-t0">{mfe.manager || "—"}</span></div>
            <div className="py-[8px] flex items-center justify-between"><span className="text-[13px] text-t2">Work Mode</span><Bg c={K.bl} sm>{mfe.work_mode || "—"}</Bg></div>
            <div className="pt-[8px] flex items-center justify-between"><span className="text-[13px] text-t2">Employment Type</span><span className="text-[14px] font-semibold text-t0">{mfe.employee_type || "—"}</span></div>
          </div>
        </Cd>
        {mfe.centre_hr && (
          <Cd>
            <div className="flex items-center gap-2 mb-[10px]"><Building2 size={16} className="text-p" /><span className="text-[15px] font-extrabold text-t0">Your Centre HR</span></div>
            <div className="bg-pg rounded-xl p-[12px]">
              <div className="flex items-center gap-[10px]">
                <Av nm={mfe.centre_hr.name || "?"} src={mfe.centre_hr.photo} sz={38} rn={K.p} />
                <div className="min-w-0">
                  <div className="text-[14px] font-bold text-t0 truncate">{mfe.centre_hr.name || "—"}</div>
                  <div className="text-[12px] text-t3">Centre HR</div>
                </div>
              </div>
              {mfe.centre_hr.email && (
                <div className="flex items-center gap-[6px] mt-[10px] pt-[10px] border-t border-b0">
                  <Mail size={14} className="text-t3 flex-shrink-0" />
                  <a href={`mailto:${mfe.centre_hr.email}`} className="font-mono text-[13px] font-semibold text-p truncate hover:underline">{mfe.centre_hr.email}</a>
                </div>
              )}
            </div>
          </Cd>
        )}
        <Cd>
          <div className="flex items-center justify-between mb-[10px]">
            <div className="flex items-center gap-2"><Phone size={16} className="text-r" /><span className="text-[15px] font-extrabold text-t0">Emergency Contact</span></div>
          </div>
          {emg.contact_name || emg.name ? <div className="bg-pg rounded-xl p-[12px]">
            <div className="flex items-center gap-[10px]">
              <Av nm={emg.contact_name || emg.name || "?"} sz={38} rn={K.r} />
              <div className="min-w-0">
                <div className="text-[14px] font-bold text-t0 truncate">{emg.contact_name || emg.name}</div>
                <div className="text-[12px] text-t3">{emg.relation || "—"}</div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-[10px] pt-[10px] border-t border-b0">
              <span className="font-mono text-[14px] font-semibold text-t1">{emg.phone ? (showEmergencyPhone ? emg.phone : maskId(emg.phone, 2, 2)) : "—"}</span>
              {emg.phone && <button onClick={() => setShowEmergencyPhone(v => !v)} className="text-p text-[13px] font-bold bg-transparent border-0 cursor-pointer hover:underline">{showEmergencyPhone ? "Hide" : "View Full"}</button>}
            </div>
          </div> : <div className="text-[13px] text-t3">No emergency contact on file. Contact HR to add one.</div>}
        </Cd>
        <Cd>
          <div className="flex items-center justify-between mb-[10px]">
            <span className="text-[15px] font-extrabold text-t0">Statutory & Compliance IDs</span>
            <ShieldCheck size={16} className="text-tl" />
          </div>
          <div className="flex flex-col gap-[8px]">
            {[
              { l: "PAN", v: mfe.salary?.pan_number, ic: CreditCard },
              { l: "Aadhaar", v: mfe.salary?.aadhar_number, ic: Fingerprint },
              { l: "UAN", v: mfe.salary?.uan_number, ic: Users },
            ].map(row => <div key={row.l} className="flex items-center justify-between p-[10px] rounded-xl bg-pg">
              <div className="flex items-center gap-2 min-w-0">
                <row.ic size={16} className="text-p flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-t0">{row.l}</div>
                  <div className="text-[12px] font-mono text-t3 truncate">{maskId(row.v) || "Not on file"}</div>
                </div>
              </div>
              {row.v && <CheckCircle size={16} className="text-g flex-shrink-0" />}
            </div>)}
          </div>
          <button onClick={() => setMyTab("salary")} className="w-full mt-[10px] py-2 rounded-xl bg-pg hover:bg-pb text-p text-[13px] font-semibold border-0 cursor-pointer flex items-center justify-center gap-1 transition-colors">
            <Lock size={13} /> View in Salary &amp; Payroll
          </button>
        </Cd>
      </div>
    </div>}
    {myTab === "employment" && <Cd>
      <Sec icon={Briefcase} title="Employment Details" color={K.p} />
      <div className="text-[13px] text-t3 -mt-[10px] mb-[12px]">Role, reporting line, and work setup as per HR records</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-[10px]">
        <Fld label="Join Date" value={mfe.join_date ? fmtDMY(mfe.join_date) : null} icon={Cake} />
        <Fld label="Employee Type" value={mfe.employee_type} />
        <Fld label="Department" value={mfe.department} />
        <Fld label="Designation" value={mfe.designation || mfe.role?.title} />
        <Fld label="Reporting Manager" value={mfe.manager} icon={Users} />
        <Fld label="Work Location" value={mfe.office?.city || mfe.office?.name} icon={MapPin} />
        <Fld label="Status" value={mfe.status || mfe.probation} />
        <Fld label="Shift" value={mfe.shift} />
        <Fld label="Work Mode" value={mfe.work_mode} />
      </div>
    </Cd>}
    {myTab === "salary" && <div className="flex flex-col gap-[14px]">
      {structure && structure.components && structure.components.length > 0 && (
        <Cd>
          <Sec icon={Wallet} title="CTC Breakdown" color={K.g} />
          <EmployeeCtcSummary structure={structure} annualCtcAnchor={structure.annualCtc} />
        </Cd>
      )}
      <Cd>
        <Sec icon={ShieldCheck} title="Statutory IDs" color={K.tl} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[10px]">
          <Fld label="UAN Number" value={mfe.salary?.uan_number} mono />
          <Fld label="PF Number" value={mfe.salary?.pf_number} mono />
          <Fld label="ESI Number" value={mfe.salary?.esi_number} mono />
          <Fld label="GHI Policy Number" value={mfe.salary?.ghi_number} mono />
        </div>
      </Cd>
      <Cd>
        <Sec icon={CreditCard} title="Tax" color={K.pu} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[10px]">
          <Fld label="PAN Number" value={mfe.salary?.pan_number} mono />
          <Fld label="Tax Regime" value={mfe.salary?.tax_regime} />
        </div>
      </Cd>
      <Cd>
        <Sec icon={Building} title="Bank Details" color={K.bl} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[10px]">
          <Fld label="Bank Name" value={mfe.salary?.bank_name} />
          <Fld label="Account Number" value={mfe.salary?.account_number} mono />
          <Fld label="IFSC Code" value={mfe.salary?.ifsc_code} mono />
          <Fld label="Payment Mode" value={mfe.salary?.payment_mode} />
        </div>
      </Cd>
      <MyDeductionsCard />
      <MyAdditionsCard />

      <Cd>
        <Sec icon={Wallet} title="Salary Revision Timeline" color={K.a} />
        {salaryHistory === null ? (
          <div className="text-[14px] text-t3">Loading…</div>
        ) : salaryHistory.length === 0 ? (
          <div className="text-[14px] text-t3">No revisions on record yet.</div>
        ) : (() => {
          const fmt = (n) => (n == null || n === '') ? '—' : '₹ ' + Number(n).toLocaleString('en-IN');
          const fmtDate = (d) => d ? fmtDMY(d) : '—';
          return <Tbl cols={[
            { k: 'eff', l: 'Effective', r: r => <span className="text-[14px]">{fmtDate(r.effective_from)} → {r.effective_to ? fmtDate(r.effective_to) : <span className="text-g font-semibold">Current</span>}</span> },
            { k: 'ctc', l: 'CTC',   r: r => <span className="text-[14px] font-semibold text-t0">{fmt(r.ctc_annual)}</span> },
            { k: 'bas', l: 'Basic', r: r => <span className="text-[14px] text-t2">{fmt(r.basic_salary)}</span> },
            { k: 'hra', l: 'HRA',   r: r => <span className="text-[14px] text-t2">{fmt(r.hra)}</span> },
            { k: 'all', l: 'Allow', r: r => <span className="text-[14px] text-t2">{fmt(r.allowances)}</span> },
            { k: 'rsn', l: 'Reason', r: r => <span className="text-[12px] text-t3">{r.reason || '—'}</span> },
          ]} data={salaryHistory} />;
        })()}
      </Cd>

      <Cd>
        <Sec icon={FileText} title="Salary Slips" color={K.g} />
        {(() => {
          if (mySlips === null) return <div className="text-[14px] text-t3">Loading…</div>;
          if (mySlips.length === 0) return <div className="text-[14px] text-t3">No payslips published yet. Once HR runs payroll and generates slips, they'll show up here.</div>;
          const fmtPeriod = (p) => {
            const [y, m] = p.split('-');
            return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
          };
          const fmtINR = (n) => '₹' + Number(Math.round(n || 0)).toLocaleString('en-IN');
          const picked = mySlips.find(s => s.period === pickedPeriod);
          const net = picked ? (picked.net || picked.baseNet || 0) : 0;
          return <div className="flex items-center gap-2 flex-wrap">
            <div className="min-w-[220px] max-w-[300px] flex-1">
              <Sl
                options={mySlips.map(s => ({ v: s.period, l: fmtPeriod(s.period) + ' · ' + fmtINR(s.net || s.baseNet || 0) }))}
                value={pickedPeriod}
                onChange={setPickedPeriod}
              />
            </div>
            <Bt s="sm" icon={Download} disabled={!pickedPeriod}
                onClick={() => window.open(`${BASE}/api/payroll/slips/${pickedPeriod}/pdf?employee_id=${mfe.employee_id}&token=${localStorage.getItem("hrms_token")}`, "_blank")}>
              Download PDF
            </Bt>
            {picked && (
              <div className="text-[14px] text-t3">
                Net pay <b className="text-t0">{fmtINR(net)}</b>
                {picked.incentive ? <> · incentive {fmtINR(picked.incentive)}</> : null}
                {(picked.lopDays || picked.lop_days) > 0 && (
                  <> · LOP {(picked.lopDays || picked.lop_days)}d
                    {(picked.lopDaysLwp > 0 || picked.lopDaysAbsent > 0) && (
                      <> (
                        {picked.lopDaysLwp > 0 && <>Approved LWP {picked.lopDaysLwp}d</>}
                        {picked.lopDaysLwp > 0 && picked.lopDaysAbsent > 0 && <> · </>}
                        {picked.lopDaysAbsent > 0 && <>Absent {picked.lopDaysAbsent}d</>}
                      )</>
                    )}
                  </>
                )}
              </div>
            )}
          </div>;
        })()}
      </Cd>

      <Cd>
        <Sec icon={CreditCard} title="TDS & Tax Details" color={K.r} />
        {(() => { const fy = new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1; const loadTax = () => api.get(`/api/payroll/tax-summary/${taxFY}`).then(d => setTaxData(d)).catch(() => setTaxData(null)); return <div>
          <div className="flex gap-[6px] items-center mb-[10px]">
            <Sl options={[{ v: String(fy), l: `FY ${fy}-${fy + 1}` }, { v: String(fy - 1), l: `FY ${fy - 1}-${fy}` }]} value={taxFY} onChange={v => { setTaxFY(v); setTaxData(null); }} />
            <Bt s="sm" onClick={loadTax}>Load Tax Summary</Bt>
          </div>
          {taxData && <div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-[10px] mb-3">
              <div className="bg-pg rounded-xl px-[14px] py-[10px] text-center"><div className="text-[11px] uppercase tracking-wide text-t3">Gross Income</div><div className="text-[15px] font-extrabold text-t0 mt-[2px]">₹{Number(taxData.totals?.grossIncome || 0).toLocaleString('en-IN')}</div></div>
              <div className="bg-pg rounded-xl px-[14px] py-[10px] text-center"><div className="text-[11px] uppercase tracking-wide text-t3">Total TDS</div><div className="text-[15px] font-extrabold text-r mt-[2px]">₹{Number(taxData.totals?.tds || 0).toLocaleString('en-IN')}</div></div>
              <div className="bg-pg rounded-xl px-[14px] py-[10px] text-center"><div className="text-[11px] uppercase tracking-wide text-t3">Total Deductions</div><div className="text-[15px] font-extrabold text-a mt-[2px]">₹{Number(taxData.totals?.totalDeductions || 0).toLocaleString('en-IN')}</div></div>
              <div className="bg-pg rounded-xl px-[14px] py-[10px] text-center"><div className="text-[11px] uppercase tracking-wide text-t3">Net Pay</div><div className="text-[15px] font-extrabold text-g mt-[2px]">₹{Number(taxData.totals?.netPay || 0).toLocaleString('en-IN')}</div></div>
            </div>
            <div className="text-[14px] font-bold text-t0 mb-[6px]">Monthly TDS Breakdown</div>
            {taxData.months && taxData.months.length > 0 ? <Tbl cols={[{ k: "period", l: "Month" }, { k: "gross", l: "Gross", r: r => <span>₹{Number(r.gross || 0).toLocaleString('en-IN')}</span> }, { k: "tds", l: "TDS", r: r => <span className="text-r">₹{Number(r.tds || 0).toLocaleString('en-IN')}</span> }, { k: "net", l: "Net Pay", r: r => <span className="text-g">₹{Number(r.net || 0).toLocaleString('en-IN')}</span> }]} data={taxData.months} />
              : <div className="text-[14px] text-t3 p-3">No payroll data for this financial year.</div>}
            <div className="mt-[10px] text-[14px] text-t3">Form 130 data shown above. PAN: {taxData.employee?.pan || "—"} | UAN: {taxData.employee?.uan || "—"}</div>
          </div>}
          {!taxData && <div className="text-[14px] text-t3">Click "Load Tax Summary" to view TDS details, Form 130, and ITR summary.</div>}
        </div>; })()}
      </Cd>
      <div className="text-[14px] text-t3">Contact HR or Accounts for any salary-related queries.</div>
    </div>}
    {myTab === "documents" && <Cd>
      <Sec icon={FolderOpen} title="My Documents" color={K.bl} />
      {mfe.documents.length > 0 ? <Tbl cols={[
        { k: "type", l: "Document", r: r => <div className="flex items-center gap-[6px]"><FileText size={14} color={K.bl} /><span className="font-semibold text-[14px]">{r.doc_type || r.type || ""}</span></div> },
        { k: "file", l: "File", r: r => r.file_path ? <Bt v="success" s="xs" icon={Download} onClick={() => window.open(uploadUrl(r.file_path), "_blank")}>{r.file_name || "Download"}</Bt> : <Bg c={K.r} sm>Not Uploaded</Bg> },
        { k: "at", l: "Uploaded", r: r => <span className="text-[14px] text-t2">{r.uploaded_at ? fmtDMY(r.uploaded_at) : "—"}</span> },
      ]} data={mfe.documents} /> : <div className="text-center p-[30px] text-t3">No documents uploaded. Contact HR to upload your documents.</div>}
    </Cd>}
    {myTab === "ghi" && <Cd>
      <Sec icon={Heart} title="Group Health Insurance" color={K.r} />
      {!ghi ? (
        <div className="text-center p-6 text-t3 text-[14px]">Loading…</div>
      ) : ghi.status === 'Not Enrolled' ? (
        <div className="text-[14px] text-t3 p-3 rounded-xl bg-pg">
          You are not currently enrolled under the Group Health Insurance policy. Reach out to HR if you'd like to be added.
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-[10px] mb-5">
            <Fld label="GHI Number" value={ghi.policyNumber} mono />
            <Fld label="Status">
              {ghi.status === 'Active'
                ? <span className="text-[14px] font-semibold text-g">Active</span>
                : <span className="text-[14px] font-semibold text-a">In Progress</span>}
            </Fld>
            <Fld label="Sum Insured (Coverage)" value={ghi.sumInsured ? '₹' + Number(ghi.sumInsured).toLocaleString('en-IN') : null} />
          </div>
          {ghi.status === 'In Progress' && (
            <div className="text-[14px] text-a mb-4 p-2 rounded bg-ab border border-ad">
              Your policy is being activated — HR is adding you to the insurer's portal. Coverage kicks in once this is confirmed.
            </div>
          )}
          {ghi.bankEnrolledAt && (
            <div className="text-[12px] text-t3 mb-3">
              Enrolled on insurer's portal on {fmtDMY(ghi.bankEnrolledAt)}
              {ghi.bankEnrolledByName ? ` by ${ghi.bankEnrolledByName}` : ''}.
            </div>
          )}
          <div className="text-[14px] font-bold text-t0 mb-2 flex items-center gap-2">
            <Heart size={13} color={K.r} /> Family Members ({ghi.family.length})
          </div>
          {ghi.family.length === 0 ? (
            <div className="text-center p-8 text-t3 text-[14px] bg-pg rounded-xl">No family members added yet. Contact HR to add a dependent.</div>
          ) : (
            <div className="grid gap-2">
              {ghi.family.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-pg flex-wrap">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {m.photo_path ? (
                      <img src={uploadUrl(m.photo_path)} alt={m.name} className="w-10 h-10 rounded-full object-cover border border-b0" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-bg1 border border-b0 flex items-center justify-center text-[14px] font-bold text-t3">{(m.name || '?')[0]}</div>
                    )}
                    <div className="min-w-0">
                      <div className="text-[14px] font-bold text-t0 truncate">{m.name}</div>
                      <div className="text-[12px] text-t3">{m.relationship}{m.dob ? ' · ' + fmtDMY(m.dob) : ''}{m.gender ? ' · ' + m.gender : ''}</div>
                    </div>
                  </div>
                  {m.document_path && <Bt v="ghost" s="xs" icon={Download} onClick={() => window.open(uploadUrl(m.document_path), '_blank')}>Document</Bt>}
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 text-[14px] text-t3">This tab is view-only — contact HR to add, update, or remove a dependent.</div>
        </div>
      )}
    </Cd>}
    {myTab === "emergency" && <Cd>
      <Sec icon={Phone} title="Emergency Contact" color={K.r} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
        <Fld label="Contact Name" value={mfe.emergency?.contact_name || mfe.emergency?.name} />
        <Fld label="Relation" value={mfe.emergency?.relation} />
        <Fld label="Phone" value={mfe.emergency?.phone} icon={Phone} mono />
        <Fld label="Address" value={mfe.emergency?.address} icon={MapPin} />
      </div>
      <div className="mt-[12px] text-[13px] text-t3">Contact HR to update emergency contact information.</div>
    </Cd>}
    {myTab === "education" && <Cd>
      <Sec icon={GraduationCap} title="Education" color={K.tl} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[10px]">
        <Fld label="Highest Qualification" value={mfe.education?.highest_qualification} />
        <Fld label="Degree" value={mfe.education?.degree} />
        <Fld label="Specialization" value={mfe.education?.specialization} />
        <Fld label="University / College" value={mfe.education?.university} />
        <Fld label="Passing Year" value={mfe.education?.passing_year} />
        <Fld label="Certifications" value={mfe.education?.certifications} />
        <Fld label="Skills" value={mfe.education?.skills} />
      </div>
    </Cd>}
    {myTab === "experience" && (() => {
      const calcDur = (sd, ed) => { if (!sd || !ed) return ""; const s = new Date(sd), e = new Date(ed); if (isNaN(s) || isNaN(e) || s > e) return ""; let yrs = e.getFullYear() - s.getFullYear(); let mos = e.getMonth() - s.getMonth(); if (mos < 0) { yrs--; mos += 12; } return yrs > 0 ? (mos > 0 ? yrs + " Year" + (yrs > 1 ? "s" : "") + " " + mos + " Month" + (mos > 1 ? "s" : "") : yrs + " Year" + (yrs > 1 ? "s" : "")) : (mos > 0 ? mos + " Month" + (mos > 1 ? "s" : "") : "Less than a month"); };
      return <Cd>
        <Sec icon={Building} title="Work Experience" color={K.p} />
        {mfe.experience.length > 0 ? <div className="flex flex-col gap-[10px]">{mfe.experience.map((ex, idx) => { const dur = calcDur((ex.start_date || "").slice(0, 10), (ex.end_date || "").slice(0, 10)) || ex.total_experience || ""; return <div key={ex.id || idx} className="bg-pg rounded-xl p-[14px]">
          <div className="text-[15px] font-bold text-t0">{ex.company_name || ex.company || "—"}</div>
          <div className="text-[14px] text-t2 mt-[2px]">{ex.designation || "—"}</div>
          <div className="flex gap-3 mt-[8px] flex-wrap">
            <div><div className="text-[11px] uppercase text-t3">From</div><div className="text-[14px] font-semibold text-t1">{ex.start_date ? fmtDMY(ex.start_date) : "—"}</div></div>
            <div><div className="text-[11px] uppercase text-t3">To</div><div className="text-[14px] font-semibold text-t1">{ex.end_date ? fmtDMY(ex.end_date) : "Present"}</div></div>
            {ex.last_salary && <div><div className="text-[11px] uppercase text-t3">Last CTC</div><div className="text-[14px] font-semibold text-t0">₹{Number(ex.last_salary).toLocaleString('en-IN')}</div></div>}
            <div><div className="text-[11px] uppercase text-t3">Duration</div><div className="text-[14px] font-semibold text-t1">{dur || "—"}</div></div>
          </div>
        </div>; })}</div> : <div className="text-center p-[30px] text-t3">No experience records.</div>}
      </Cd>;
    })()}
    {myTab === "myteam" && (() => {
      const counts = teamNode
        ? teamNode.directReports.reduce((acc, r) => {
            const k = r.presence?.state || "unknown";
            acc[k] = (acc[k] || 0) + 1;
            return acc;
          }, {})
        : {};

      return (
        <Cd style={{ padding: 0, overflow: "hidden" }}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-b0 flex-wrap gap-3">
            <Sec icon={Users} title={effectiveTeamId === myId ? "My Team" : `${teamNode ? teamNode.self.name + "'s" : "Their"} Team`} color={K.p} />
            <div className="flex items-center gap-2 flex-wrap">
              {canBrowseOrgTree && (
                <div className="w-[240px]">
                  <SearchSelect
                    options={(orgEmployees || []).map(e => ({ v: e.id, l: e.nm || e.name || e.employee_name, sub: e.dept || e.role }))}
                    value=""
                    onChange={v => { if (v) setTeamFocusId(v); }}
                    placeholder="View someone else's team…"
                    searchPlaceholder="Search by name or ID…"
                  />
                </div>
              )}
              {teamNode && (
                <span className="text-[13px] font-semibold text-t2 bg-bg2 border border-b0 rounded-lg px-[10px] py-[5px]">
                  Members <span className="text-t0 font-bold ml-[2px]">{teamNode.directReports.length}</span>
                </span>
              )}
              {effectiveTeamId !== myId && (
                <Bt v="ghost" s="sm" onClick={() => setTeamFocusId(null)}>← Back to my team</Bt>
              )}
            </div>
          </div>

          {/* Roll-up of who's around right now, so a manager doesn't have
               to count coloured dots across the tree. */}
          {teamNode && teamNode.directReports.length > 0 && (
            <div className="flex items-center gap-2 px-5 py-3 flex-wrap border-b border-b0">
              {Object.entries(TEAM_PRESENCE)
                .filter(([k]) => counts[k])
                .map(([k, cfg]) => (
                  <span key={k} className="flex items-center gap-[6px] text-[13px] font-semibold px-[10px] py-[4px] rounded-full border"
                        style={{ color: cfg.c, borderColor: `${cfg.c}33`, background: `${cfg.c}0d` }}>
                    <span className="w-[6px] h-[6px] rounded-full" style={{ background: cfg.c }} />
                    {cfg.label} {counts[k]}
                  </span>
                ))}
            </div>
          )}

          {/* Org chart canvas — manager above, you (or whoever's focused)
               in the middle, direct reports branching below. Click the
               manager card to walk up the chain; click a report card with
               its own reports to expand THEIR reports as a fresh branch
               right below it, in place — keep clicking down as many
               levels as the chain actually goes. Each level's data is
               fetched (and cached) lazily on first expand. */}
          <div
            className="relative px-6 py-10"
            style={{ backgroundColor: K.bg2, backgroundImage: `radial-gradient(${K.b1} 1px, transparent 1px)`, backgroundSize: "22px 22px" }}
          >
            {teamNode === null && <div className="text-center p-[40px] text-t3 text-[14px]">Loading…</div>}
            {teamNode === false && <div className="text-center p-[40px] text-t3 text-[14px]">Couldn't load this person's team.</div>}
            {teamNode && (
              <div className="flex flex-col items-center">
                {teamNode.manager ? (
                  <>
                    <TeamSectionLabel>Reports To</TeamSectionLabel>
                    <TeamNodeCard node={teamNode.manager} tier="manager" onClick={() => setTeamFocusId(teamNode.manager.id)} />
                    <TeamStem />
                  </>
                ) : (
                  <div className="text-[13px] text-t3 mb-4 italic">No reporting manager on file.</div>
                )}
                <TeamNodeCard node={teamNode.self} tier="self" />
                {teamNode.directReports.length > 0 && (
                  <>
                    <TeamStem h={26} />
                    <TeamSectionLabel>
                      {teamNode.directReports.length} Direct Report{teamNode.directReports.length === 1 ? "" : "s"}
                    </TeamSectionLabel>
                    <div className="flex flex-wrap justify-center gap-x-5 gap-y-8 pt-5" style={{ borderTop: `2px solid ${K.b1}` }}>
                      {teamNode.directReports.map(r => (
                        <div key={r.id} className="relative">
                          <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-[2px] h-5" style={{ background: K.b1 }} />
                          <TeamTreeBranch node={r} />
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {teamNode.directReports.length === 0 && (
                  <div className="text-[13px] text-t3 mt-4 italic">No direct reports.</div>
                )}
              </div>
            )}
          </div>
        </Cd>
      );
    })()}
    {myTab === "security" && <div className="max-w-[480px]">
      <Cd style={{ padding: 18 }}>
        <div className="flex items-center gap-2 mb-1">
          <Lock size={16} color={K.p} />
          <div className="text-[15px] font-bold text-t0">Change Password</div>
        </div>
        <div className="text-[14px] text-t3 mb-[14px]">Change your password whenever you like — you don't need HR for this. Minimum 8 characters with at least one letter and one number.</div>
        {mustChangePassword && !pwdForm.msg && (
          <div className="text-[14px] text-a bg-ab border border-ad rounded-lg px-3 py-2 mb-[14px]">
            HR issued you a temporary password. Please set your own here.
          </div>
        )}
        {pwdForm.msg && <div className="px-[10px] py-2 rounded-lg text-[14px] mb-[10px] flex items-center gap-[6px] bg-g/20 border border-g text-g"><CheckCircle size={12} />{pwdForm.msg}</div>}
        {pwdForm.err && <div className="px-[10px] py-2 rounded-lg text-[14px] mb-[10px] flex items-center gap-[6px] bg-r/20 border border-r !border-r-[#E5E7EB] text-r"><AlertCircle size={12} />{pwdForm.err}</div>}
        <div className="flex flex-col gap-[10px]">
          <div>
            <div className="text-[12px] text-t3 uppercase mb-[3px]">Current Password</div>
            <div className="relative">
              <input type={pwdForm.showCur ? "text" : "password"} value={pwdForm.current} onChange={e => setPwdForm(p => ({ ...p, current: e.target.value, msg: "", err: "" }))} placeholder="Enter current password" className="w-full px-[10px] py-2 pr-8 bg-bg2 border border-b0 rounded-lg text-[14px] text-t0 box-border outline-none" />
              <button type="button" onClick={() => setPwdForm(p => ({ ...p, showCur: !p.showCur }))} aria-label={pwdForm.showCur ? "Hide password" : "Show password"}
                className="absolute right-[8px] top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-t3 p-1">
                {pwdForm.showCur ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <div className="text-[12px] text-t3 uppercase mb-[3px]">New Password</div>
            <div className="relative">
              <input type={pwdForm.showNext ? "text" : "password"} value={pwdForm.next} onChange={e => setPwdForm(p => ({ ...p, next: e.target.value, msg: "", err: "" }))} placeholder="At least 8 characters" className="w-full px-[10px] py-2 pr-8 bg-bg2 border border-b0 rounded-lg text-[14px] text-t0 box-border outline-none" />
              <button type="button" onClick={() => setPwdForm(p => ({ ...p, showNext: !p.showNext }))} aria-label={pwdForm.showNext ? "Hide password" : "Show password"}
                className="absolute right-[8px] top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-t3 p-1">
                {pwdForm.showNext ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <div className="text-[12px] text-t3 uppercase mb-[3px]">Confirm New Password</div>
            <div className="relative">
              <input type={pwdForm.showConfirm ? "text" : "password"} value={pwdForm.confirm} onChange={e => setPwdForm(p => ({ ...p, confirm: e.target.value, msg: "", err: "" }))} placeholder="Re-enter new password" className="w-full px-[10px] py-2 pr-8 bg-bg2 border border-b0 rounded-lg text-[14px] text-t0 box-border outline-none" />
              <button type="button" onClick={() => setPwdForm(p => ({ ...p, showConfirm: !p.showConfirm }))} aria-label={pwdForm.showConfirm ? "Hide password" : "Show password"}
                className="absolute right-[8px] top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-t3 p-1">
                {pwdForm.showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div className="flex gap-2 mt-1">
            <Bt v="secondary" onClick={() => setPwdForm({ current: "", next: "", confirm: "", showCur: false, showNext: false, showConfirm: false, saving: false, msg: "", err: "" })} disabled={pwdForm.saving}>Clear</Bt>
            <Bt icon={Lock} disabled={pwdForm.saving || !pwdForm.current || !pwdForm.next || !pwdForm.confirm} onClick={async () => {
              if (pwdForm.next !== pwdForm.confirm) { setPwdForm(p => ({ ...p, err: "New password and confirmation do not match" })); return; }
              if (pwdForm.next.length < 8) { setPwdForm(p => ({ ...p, err: "New password must be at least 8 characters" })); return; }
              if (!/[A-Za-z]/.test(pwdForm.next) || !/\d/.test(pwdForm.next)) { setPwdForm(p => ({ ...p, err: "New password must contain at least one letter and one number" })); return; }
              if (pwdForm.current === pwdForm.next) { setPwdForm(p => ({ ...p, err: "New password must be different from current password" })); return; }
              setPwdForm(p => ({ ...p, saving: true, err: "", msg: "" }));
              try {
                await api.post('/auth/change-password', { current_password: pwdForm.current, new_password: pwdForm.next });
                setPwdForm({ current: "", next: "", confirm: "", showCur: false, showNext: false, showConfirm: false, saving: false, msg: "Password changed successfully.", err: "" });
              } catch (e) { setPwdForm(p => ({ ...p, saving: false, err: e.message || "Failed to change password" })); }
            }}>{pwdForm.saving ? "Changing..." : "Change Password"}</Bt>
          </div>
        </div>
        <div className="text-[12px] text-t3 mt-[14px] leading-relaxed">Forgot your current password? Sign out and use “Forgot password?” on the sign-in page to reset it yourself, or ask HR to send you a reset — you'll then be asked to choose a new one when you sign in.</div>
      </Cd>
    </div>}
    {photoCrop && photoCrop._myProfile && <PhotoUploadModal photoCrop={photoCrop} setPhotoCrop={setPhotoCrop} onSaved={onPhotoUpdated} />}
  </div>;
};
